import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureInteger } from "../../common/utils/generalUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { logout } from "../../common/reducers/initContext";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = group => ensureInteger(group.id);

export const shiftGroupStaffSlice = createSlice({
    name: 'staffShiftGroups',
    initialState: {
        data: {}
    },

    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(group => state.data[key(group)] = group);
        },
        setForShift: (state, action) => {
            const toProcess = new Set()
            Object.values(state.data).forEach(g => {
                if (action.payload.shiftids.includes(g.shiftid)) {
                    toProcess.add(key(g));
                }
            })
            action.payload.data.forEach(g => {
                state.data[key(g)] = g;
                toProcess.delete(key(g));
            });
            toProcess.forEach(id => delete state.data[id]);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(g => {
                    state.data[key(g)] = g;
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(g => {
                delete state.data[key(g)];
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => shiftGroupStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shiftGroup_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shiftGroup_one');

const fetchShiftGroup = aggregatingFetch(
    id => network => {
        return network.get('/api/v2/staffShiftGroup/' + encodeURIComponent(id), data => shiftGroupStaffSlice.actions.set({ data, ids: [id] }), visualizeFailure('get'))
    },
    ids => network => network.post('/api/v2/staffShiftGroups', ids, data => shiftGroupStaffSlice.actions.set({ data, ids }), visualizeFailure('get')),
    { primitiveKey: true }
);
const fetchShiftGroupsForShift = aggregatingFetch(
    shiftid => network => network.get('/api/v2/staffShiftGroups/' + encodeURIComponent(shiftid), data => shiftGroupStaffSlice.actions.setForShift({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/staffShiftGroupsForShifts', shiftids, data => shiftGroupStaffSlice.actions.setForShift({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
);

// #region selectors
const selectShiftGroupData = state => state.staffShiftGroups.data;
const selectShiftGroup = id => createSelector([selectShiftGroupData], groups => groups[id]);
const selectShiftGroups = ids => createSelector([selectShiftGroupData], groups => ids.reduce((res, id) => {
    if (groups[id]) {
        return res.concat(groups[id])
    } 
    return res;
}, []));
const selectShiftGroupsForShift = shiftidsInput => createSelector([selectShiftGroupData], groups => {
    const shiftids = new Set(ensureArray(shiftidsInput))
    return Object.values(groups).filter(g => shiftids.has(g.shiftid))
});
// #endregion

const listen = {
    groupStaffCreated: (group, { dispatch, websocket }) => {
        markFetched(websocket, 'shift_group', key(group))
        dispatch(shiftGroupStaffSlice.actions.addOrUpdate(group))
    },
    groupStaffUpdated: (group, { dispatch }) => dispatch(shiftGroupStaffSlice.actions.addOrUpdate(group)),
    groupStaffRemoved: (group, { dispatch }) => dispatch(shiftGroupStaffSlice.actions.remove(group))
}
const options = {
    cleanup: { root: shiftGroupStaffSlice.name, all: state => Object.values(selectShiftGroupData(state)), removeAll: shiftGroupStaffSlice.actions.remove, remove: shiftGroupStaffSlice.actions.remove },
    condition: credentials => credentials?.hasRole('member')
}

const optionsSingle = { ...options, defaultValue: null}

const shiftGroupInitByID = initMapFactory(fetchShiftGroup, listen, { name: 'shiftGroupByID' });
registerEntityBasedFetch('shift_group', shiftGroupInitByID);

export const [useStaffShiftGroup] = dataHookWithCredentialsFactory(selectShiftGroup, shiftGroupInitByID, { ...options, defaultValue: null });
export const [useStaffShiftGroups] = dataHookWithCredentialsFactory(selectShiftGroups, initMultiMapFactory(shiftGroupInitByID, listen), options);

const shiftGroupInitByShift = queryMapFactory(fetchShiftGroupsForShift, 'id', shiftGroupInitByID, listen);
registerEntityBasedFetch('shift', shiftGroupInitByShift);

export const [useStaffShiftGroupsForShift] = dataHookWithCredentialsFactory(selectShiftGroupsForShift, shiftGroupInitByShift, options);
export const [useStaffShiftGroupsForShifts] = dataHookWithCredentialsFactory(selectShiftGroupsForShift, initMultiMapFactory(shiftGroupInitByShift, listen), options);

export const [useReadStaffShiftGroup] = readonlyHookWithCredentialsFactory(selectShiftGroup, id => shiftGroupInitByShift, optionsSingle);
export const [useReadStaffShiftGroups] = readonlyHookWithCredentialsFactory(selectShiftGroups, ids => shiftGroupInitByShift, options);


const reducer = { name: shiftGroupStaffSlice.name, reducer: shiftGroupStaffSlice.reducer }
export default reducer;